import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useParams } from 'react-router-dom';
import { METADATA } from '@flexo/reducers';
import { getOrgRole, isEndUser } from './roles';

type EndUserSiteGuardProps = {
  children: React.ReactNode;
};

/**
 * FLEXO-6090 / FLEXO-6089: data-isolation guard for end-user site routes.
 *
 * An end user may only view a site that belongs to their own entity set
 * (`/site/:id` where `:id` is one of their authorised site IDs, derived from
 * `metadata.entities`). Any attempt to open a foreign site via direct URL is
 * redirected to their own member aggregate. Staff (admin/supervisor) are
 * unaffected — the guard is a no-op for non-end-user roles.
 *
 * The guard owns the entities preflight for deep links: do not render the
 * guarded module until ownership can be decided, otherwise module resolvers can
 * fire backend calls for foreign IDs before the redirect happens.
 */
export function EndUserSiteGuard({ children }: EndUserSiteGuardProps) {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);
  const metadata = useSelector((state: any) => state.metadata);
  const { id } = useParams();

  const role = getOrgRole(user);
  const entities = (metadata?.entities as any) || null;
  const sites: any[] = entities?.site || entities?.sites || [];
  const entitiesStatus = metadata?.EntitiesAsyncStatus;
  const ownMemberId =
    user?.metadata?.flexo_user_id || user?.tokenContent?.flexo_user_id;
  const entitiesReady = entitiesStatus === 'SUCCESS' || sites.length > 0;

  useEffect(() => {
    if (
      isEndUser(role) &&
      !entitiesReady &&
      entitiesStatus !== 'PENDING' &&
      entitiesStatus !== 'ERROR'
    ) {
      dispatch(METADATA.SetEntitiesAsync({ dispatch }) as any);
    }
  }, [dispatch, entitiesReady, entitiesStatus, role]);

  if (!isEndUser(role)) {
    return <>{children}</>;
  }

  if (!entitiesReady) {
    if (entitiesStatus === 'ERROR') {
      return (
        <Navigate
          to={ownMemberId ? `/member/${ownMemberId}` : '/access-denied'}
          replace
        />
      );
    }
    return null;
  }

  const ownedSiteIds = new Set<string>(
    sites.map((s: any) => s?.siteId).filter(Boolean),
  );

  if (id && !ownedSiteIds.has(id)) {
    return (
      <Navigate
        to={ownMemberId ? `/member/${ownMemberId}` : '/access-denied'}
        replace
      />
    );
  }

  return <>{children}</>;
}
